import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Clock } from 'lucide-react';
import type { BlogPostLegacy } from '../../api/types';

const RecentBlogPosts: React.FC<{ posts?: BlogPostLegacy[] }> = ({ posts = [] }) => {
  if (!posts || posts.length === 0) return null;

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-12">
          <div>
            <span className="font-sans text-sm text-[#9CAF88] font-medium tracking-wider uppercase mb-2 block">From the Blog</span>
            <h2 className="font-serif text-4xl sm:text-5xl font-semibold text-[#3D3D3D]">Creative Inspiration</h2>
          </div>
          <Link to="/blog" className="mt-4 sm:mt-0">
            <button className="text-[#3D3D3D] hover:text-[#D4A5A5] font-sans group">Read all posts<ArrowRight className="ml-2 w-4 h-4 inline-block group-hover:translate-x-1 transition-transform" /></button>
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {posts.slice(0,3).map((post, index) => (
            <motion.div key={post.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.1 }}>
              <Link to={`/blog-post?id=${post.id}`}>
                <article className="group">
                  <div className="relative overflow-hidden rounded-2xl mb-5">
                    <img src={post.image_url} alt={post.title} className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500" />
                    {post.category && (
                      <div className="absolute top-4 left-4">
                        <span className="bg-white/90 backdrop-blur-sm text-[#3D3D3D] px-3 py-1 rounded-full font-sans text-xs font-medium capitalize">{post.category.replace('-', ' ')}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 text-[#3D3D3D]/50 font-sans text-sm mb-3">
                    <Clock className="w-4 h-4" />
                    <span>{post.created_date && new Date(post.created_date).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                  </div>

                  <h3 className="font-serif text-xl font-semibold text-[#3D3D3D] mb-3 group-hover:text-[#D4A5A5] transition-colors line-clamp-2">{post.title}</h3>
                  {post.excerpt && (<p className="font-sans text-[#3D3D3D]/70 text-sm line-clamp-2">{post.excerpt}</p>)}

                  <span className="inline-flex items-center gap-2 font-sans text-sm font-medium text-[#D4A5A5] mt-4 group-hover:gap-3 transition-all">Read More<ArrowRight className="w-4 h-4" /></span>
                </article>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RecentBlogPosts;
